import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

const AdvancedFilterSidebar = ({ isOpen, onClose, onApplyFilters, currentFilters }) => {
  const [filters, setFilters] = useState({
    salesRange: { min: '', max: '' },
    collectionRange: { min: '', max: '' },
    growthRange: { min: '', max: '' },
    regions: [],
    performanceCategories: [],
    customDateStart: '',
    customDateEnd: '',
    ...currentFilters
  });

  const regionOptions = [
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east-africa', label: 'Middle East & Africa' }
  ];

  const performanceCategoryOptions = [
    { value: 'excellent', label: 'Excellent (>120% target)' },
    { value: 'good', label: 'Good (100-120% target)' },
    { value: 'average', label: 'Average (80-100% target)' },
    { value: 'poor', label: 'Poor (<80% target)' }
  ];

  const handleRangeChange = (rangeType, field, value) => {
    setFilters(prev => ({
      ...prev,
      [rangeType]: {
        ...prev[rangeType],
        [field]: value
      }
    }));
  };

  const handleMultiSelectChange = (field, value, checked) => {
    setFilters(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters = {
      salesRange: { min: '', max: '' },
      collectionRange: { min: '', max: '' },
      growthRange: { min: '', max: '' },
      regions: [],
      performanceCategories: [],
      customDateStart: '',
      customDateEnd: ''
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`fixed lg:relative top-0 right-0 h-full w-80 bg-card border-l border-border z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Advanced Filters</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Filter Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Sales Range */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Sales Range (USD)</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.salesRange.min}
                  onChange={(e) => handleRangeChange('salesRange', 'min', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.salesRange.max}
                  onChange={(e) => handleRangeChange('salesRange', 'max', e.target.value)}
                />
              </div>
            </div>

            {/* Collection Range */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Collection Rate (%)</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min %"
                  value={filters.collectionRange.min}
                  onChange={(e) => handleRangeChange('collectionRange', 'min', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Max %"
                  value={filters.collectionRange.max}
                  onChange={(e) => handleRangeChange('collectionRange', 'max', e.target.value)}
                />
              </div>
            </div>

            {/* Growth Range */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Growth Rate (%)</h4>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min %"
                  value={filters.growthRange.min}
                  onChange={(e) => handleRangeChange('growthRange', 'min', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Max %"
                  value={filters.growthRange.max}
                  onChange={(e) => handleRangeChange('growthRange', 'max', e.target.value)}
                />
              </div>
            </div>

            {/* Regions */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Regions</h4>
              <div className="space-y-2">
                {regionOptions.map((region) => (
                  <Checkbox
                    key={region.value}
                    label={region.label}
                    checked={filters.regions.includes(region.value)}
                    onChange={(e) => handleMultiSelectChange('regions', region.value, e.target.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Performance Categories */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Performance Categories</h4>
              <div className="space-y-2">
                {performanceCategoryOptions.map((category) => (
                  <Checkbox
                    key={category.value}
                    label={category.label}
                    checked={filters.performanceCategories.includes(category.value)}
                    onChange={(e) => handleMultiSelectChange('performanceCategories', category.value, e.target.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Custom Date Range */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Custom Date Range</h4>
              <div className="space-y-2">
                <Input
                  type="date"
                  label="Start Date"
                  value={filters.customDateStart}
                  onChange={(e) => setFilters(prev => ({ ...prev, customDateStart: e.target.value }))}
                />
                <Input
                  type="date"
                  label="End Date"
                  value={filters.customDateEnd}
                  onChange={(e) => setFilters(prev => ({ ...prev, customDateEnd: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-border">
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={handleReset} fullWidth>
                Reset
              </Button>
              <Button size="sm" onClick={handleApply} fullWidth>
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvancedFilterSidebar;